import { useEffect, useRef, useState } from "react";
import CompanyService, { DataType } from "../../../services/CompanyService";
import EmailTemplateService, {
  EmailTemplateListResponse
} from "../../../services/EmailTemplateService";
import { formatDateDigits } from "../../../utils/FormatUtils";
import Dropdown from "../../shared/components/Dropdown";
import {
  AutocompleteClient,
  AutocompleteGroup,
  CheckBoxEditor,
  DatePickerEditor,
  FileListEditor,
  FileSingleEditor,
  FileApi,
  TextEditor,
  NumberInput
} from "../../shared/components/Editors";
import { TranslationService } from "../../../services/TranslationService";
import {
  RequiredManager,
  ValidationMessage,
  ValidationMessageProps
} from "../../shared/RequieredManager";
import { ToastService } from "../../shared/bootstrap/Toast";
import FloatingPanelService from "../../shared/FloatingPanel";
import ModalService from "../../shared/bootstrap/modal/Modal";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import FileService from "../../../services/FileService";
import TooltipComponent from "../../shared/TooltipComponent";
import { Editor as EmailEditor } from "react-draft-wysiwyg";
import htmlToDraft from "html-to-draftjs";
import { ContentState, convertToRaw, EditorState, Modifier } from "draft-js";
import { Cast } from "../../../utils/Utils";
import draftToHtml from "draftjs-to-html";
import { ToolbarOptions } from "../../client/components/ToolbarOptions";
import { useInsertFieldOptions } from "../../../hooks/dropdowns/useInsertFieldOptions";

type EmailTemplateItem = EmailTemplateListResponse["list"][number];

const toolbar: ToolbarOptions = {
  options: [
    "fontSize",
    "colorPicker",
    "inline",
    "list",
    "textAlign",
    "link",
    "remove"
  ],
  inline: {
    inDropdown: false,
    className: undefined,
    component: undefined,
    dropdownClassName: undefined,
    options: ["bold", "italic", "underline"],
    bold: { className: undefined, title: undefined },
    italic: { className: undefined, title: undefined },
    underline: { className: undefined, title: undefined },
    strikethrough: {
      className: undefined,
      title: undefined
    },
    monospace: { className: undefined, title: undefined },
    superscript: { className: undefined, title: undefined },
    subscript: { className: undefined, title: undefined }
  },
  blockType: {
    inDropdown: true,
    options: [
      "Normal",
      "H1",
      "H2",
      "H3",
      "H4",
      "H5",
      "H6",
      "Blockquote",
      "Code"
    ],
    className: undefined,
    component: undefined,
    dropdownClassName: undefined,
    title: undefined
  },
  fontSize: {
    options: [8, 10, 12, 14, 18, 24, 36],
    className: undefined,
    component: undefined,
    dropdownClassName: undefined,
    title: undefined
  },
  fontFamily: {
    options: [
      "Arial",
      "Georgia",
      "Impact",
      "Tahoma",
      "Times New Roman",
      "Verdana"
    ],
    className: undefined,
    component: undefined,
    dropdownClassName: undefined,
    title: undefined
  },
  list: {
    inDropdown: false,
    className: undefined,
    component: undefined,
    dropdownClassName: undefined,
    options: ["unordered", "ordered"],
    unordered: { className: undefined, title: undefined },
    ordered: { className: undefined, title: undefined },
    indent: { className: undefined, title: undefined },
    outdent: { className: undefined, title: undefined },
    title: undefined
  },
  textAlign: {
    inDropdown: false,
    className: undefined,
    component: undefined,
    dropdownClassName: undefined,
    options: ["left", "center", "right"],
    left: { className: undefined, title: undefined },
    center: { className: undefined, title: undefined },
    right: { className: undefined, title: undefined },
    justify: { className: undefined, title: undefined },
    title: undefined
  },
  colorPicker: {
    className: undefined,
    component: undefined,
    popupClassName: undefined,
    colors: [
      "rgb(0 0 0)",
      "rgb(68,68,68)",
      "rgb(102,102,102)",
      "rgb(153,153,153)",
      "rgb(204,204,204)",
      "rgb(238,238,238)",
      "rgb(243,243,243)",
      "rgb(255,255,255)",
      "rgb(255,0,0)",
      "rgb(255,153,0)",
      "rgb(255,255,0)",
      "rgb(0,255,0)",
      "rgb(0,255,255)",
      "rgb(0,0,255)",
      "rgb(153,0,255)",
      "rgb(255,0,255)",
      "rgb(244,204,204)",
      "rgb(252,229,205)",
      "rgb(255,242,204)",
      "rgb(217,234,211)",
      "rgb(208,224,227)",
      "rgb(207,226,243)",
      "rgb(217,210,233)",
      "rgb(234,209,220)",
      "rgb(234,153,153)",
      "rgb(249,203,156)",
      "rgb(255,229,153)",
      "rgb(182,215,168)",
      "rgb(162,196,201)",
      "rgb(159,197,232)",
      "rgb(180,167,214)",
      "rgb(213,166,189)",
      "rgb(224,102,102)",
      "rgb(246,178,107)",
      "rgb(255,217,102)",
      "rgb(147,196,125)",
      "rgb(118,165,175)",
      "rgb(111,168,220)",
      "rgb(142,124,195)",
      "rgb(194,123,160)",
      "rgb(204,0,0)",
      "rgb(230,145,56)",
      "rgb(241,194,50)",
      "rgb(106,168,79)",
      "rgb(69,129,142)",
      "rgb(61,133,198)",
      "rgb(103,78,167)",
      "rgb(166,77,121)",
      "rgb(153,0,0)",
      "rgb(180,95,6)",
      "rgb(191,144,0)",
      "rgb(56,118,29)",
      "rgb(19,79,92)",
      "rgb(11,83,148)",
      "rgb(53,28,117)",
      "rgb(116,27,71)",
      "rgb(102,0,0)",
      "rgb(120,63,4)",
      "rgb(127,96,0)",
      "rgb(39,78,19)",
      "rgb(12,52,61)",
      "rgb(7,55,99)",
      "rgb(32,18,77)",
      "rgb(76,17,48)"
    ],
    title: undefined
  },
  link: {
    inDropdown: false,
    className: undefined,
    component: undefined,
    popupClassName: undefined,
    dropdownClassName: undefined,
    showOpenOptionOnHover: true,
    defaultTargetOption: "_self",
    options: ["link", "unlink"],
    link: { className: undefined, title: undefined },
    unlink: { className: undefined, title: undefined },
    linkCallback: undefined
  },
  emoji: {
    className: undefined,
    component: undefined,
    popupClassName: undefined,
    emojis: [
      "😀",
      "😁",
      "😂",
      "😃",
      "😉",
      "😋",
      "😎",
      "😍",
      "😗",
      "🤗",
      "🤔",
      "😣",
      "😫",
      "😴",
      "😌",
      "🤓",
      "😛",
      "😜",
      "😠",
      "😇",
      "😷",
      "😈",
      "👻",
      "😺",
      "😸",
      "😹",
      "😻",
      "😼",
      "😽",
      "🙀",
      "🙈",
      "🙉",
      "🙊",
      "👼",
      "👮",
      "🕵",
      "💂",
      "👳",
      "🎅",
      "👸",
      "👰",
      "👲",
      "🙍",
      "🙇",
      "🚶",
      "🏃",
      "💃",
      "⛷",
      "🏂",
      "🏌",
      "🏄",
      "🚣",
      "🏊",
      "⛹",
      "🏋",
      "🚴",
      "👫",
      "💪",
      "👈",
      "👉",
      "👆",
      "🖕",
      "👇",
      "🖖",
      "🤘",
      "🖐",
      "👌",
      "👍",
      "👎",
      "✊",
      "👊",
      "👏",
      "🙌",
      "🙏",
      "🐵",
      "🐶",
      "🐇",
      "🐥",
      "🐸",
      "🐌",
      "🐛",
      "🐜",
      "🐝",
      "🍉",
      "🍄",
      "🍔",
      "🍤",
      "🍨",
      "🍪",
      "🎂",
      "🍰",
      "🍾",
      "🍷",
      "🍸",
      "🍺",
      "🌍",
      "🚑",
      "⏰",
      "🌙",
      "🌝",
      "🌞",
      "⭐",
      "🌟",
      "🌠",
      "🌨",
      "🌩",
      "⛄",
      "🔥",
      "🎄",
      "🎈",
      "🎉",
      "🎊",
      "🎁",
      "🎗",
      "🏀",
      "🏈",
      "🎲",
      "🔇",
      "🔈",
      "📣",
      "🔔",
      "🎵",
      "🎷",
      "💰",
      "🖊",
      "📅",
      "✅",
      "❎",
      "💯"
    ],
    title: undefined
  },
  embedded: {
    className: undefined,
    component: undefined,
    popupClassName: undefined,
    embedCallback: undefined,
    defaultSize: {
      height: "auto",
      width: "auto"
    },
    title: undefined
  },
  image: {
    className: undefined,
    component: undefined,
    popupClassName: undefined,
    urlEnabled: true,
    uploadEnabled: true,
    previewImage: false,
    alignmentEnabled: true,
    uploadCallback: undefined,
    inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg",
    alt: { present: false, mandatory: false },
    defaultSize: {
      height: "auto",
      width: "auto"
    },
    title: undefined
  },
  remove: {
    className: undefined,
    component: undefined,
    title: undefined
  },
  history: {
    inDropdown: false,
    className: undefined,
    component: undefined,
    dropdownClassName: undefined,
    options: ["undo", "redo"],
    undo: { className: undefined, title: undefined },
    redo: { className: undefined, title: undefined },
    title: undefined
  }
};

export type EditorType = {
  focusEditor(): unknown;
  onChange: (state: EditorState) => void;
};
const EmailTemplateEdit = ({
  reload,
  current = undefined
}: {
  reload: () => void;
  current?: EmailTemplateItem;
}) => {
  const { translate } = TranslationService;
  const [emailTemplate, setEmailTemplate] = useState<
    EmailTemplateItem & { FileLetter?: FileApi }
  >(from(current));
  const [submiting, setSubmiting] = useState("");
  const requiredManager = new RequiredManager();

  const getDefaultState = () => {
    const blocksFromHtml = htmlToDraft(emailTemplate?.Content ?? "");
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap
    );
    convertToRaw(contentState);
    return EditorState.createWithContent(contentState);
  };
  const currentEditorState = useRef(getDefaultState());
  const editorRef = useRef<null | EditorType>(null);

  const defaultOnChange = (
    fieldId: keyof EmailTemplateItem,
    required = false
  ) => {
    let onChange = (value?: string) =>
      setEmailTemplate({ ...emailTemplate, [fieldId]: value });
    if (required) {
      onChange = requiredManager.makeRequired(onChange) as (
        value?: string
      ) => void;
    }
    return onChange;
  };
  const onContentChange = defaultOnChange("Content", true);

  const onInsertField = async (text?: string) => {
    if (!text) return;

    const currentState = currentEditorState.current!;
    const selection = currentState.getSelection();

    const contentWithText = Modifier.insertText(
      currentState.getCurrentContent(),
      selection,
      text
    );

    const newEditorState = EditorState.push(
      currentState,
      contentWithText,
      "insert-characters"
    );

    const endSelection = contentWithText.getSelectionAfter();
    const finalState = EditorState.forceSelection(newEditorState, endSelection);

    currentEditorState.current = finalState;
    editorRef.current!.focusEditor();
    editorRef.current!.onChange(finalState);

    const markup = draftToHtml(convertToRaw(finalState.getCurrentContent()));
    onContentChange(markup);
  };

  const { data: insertFieldOptions } = useInsertFieldOptions({
    includeInvoices: true
  });

  const fields = [
    {
      title: translate.Template,
      onChange: defaultOnChange("Name", true),
      type: DataType.Text,
      items: [],
      defaultValue: emailTemplate?.Name,
      maxLength: 50
    },
    {
      title: translate.Subject,
      onChange: defaultOnChange("Subject", true),
      type: DataType.Text,
      items: [],
      defaultValue: emailTemplate?.Subject,
      maxLength: 300
    },
    {
      title: translate.InsertField,
      onChange: onInsertField,
      type: DataType.List,
      items: [
        { value: "", text: translate.InsertField },
        ...insertFieldOptions
      ],
      defaultValue: undefined
    },
    {
      title: "texteditor",
      onChange: onContentChange,
      type: DataType.Text,
      items: [],
      defaultValue: emailTemplate?.Content
    },
    {
      title: translate.ActivityType,
      onChange: defaultOnChange("ActivityTypeID"),
      type: DataType.List,
      items: [
        { value: "", text: translate.None },
        ...CompanyService.getActivityTypes()
          .filter((x) => x.Mail)
          .map((x) => ({ value: x.ActivityTypeID, text: x.ActivityTypeName }))
      ],
      defaultValue: current
        ? emailTemplate?.ActivityTypeID?.toString()
        : CompanyService.getActivityTypes()
            .find((x) => x.Mail && x.MailDefault)
            ?.ActivityTypeID?.toString()
    }
  ];
  const onSubmit = async () => {
    if (submiting !== "") {
      return;
    }
    if (!requiredManager.validate()) {
      ToastService.showToast(translate.MissingRequiredFields);
      return;
    }
    setSubmiting("set");
    // Mapear FileApi a la estructura esperada por el backend
    const templateToSend = {
      ...emailTemplate,
      FileLetter: emailTemplate.FileLetter
        ? {
            id: emailTemplate.FileLetter.id,
            fileName: emailTemplate.FileLetter.name,
            response: null
          }
        : undefined
    };
    const result = await EmailTemplateService.set(templateToSend);
    if (result instanceof Error) {
      ToastService.showToast(
        translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      setSubmiting("");
      return;
    }
    setSubmiting("");
    ToastService.showToast(translate.EmailTemplateSaved, undefined, "success");
    FloatingPanelService.hidePanel();
    reload();
  };
  const onDelete = () => {
    if (submiting !== "") {
      return;
    }
    const deleteEmailTemplate = async () => {
      if (submiting) {
        return;
      }
      setSubmiting("del");
      const result = await EmailTemplateService.delete(
        current!.MailTemplateID!
      );
      if (result instanceof Error) {
        ToastService.showToast(
          translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        setSubmiting("");
        return;
      }
      setSubmiting("");
      ToastService.showToast(
        translate.EmailTemplateDeleted,
        undefined,
        "success"
      );
      FloatingPanelService.hidePanel();
      reload();
    };
    ModalService.showDefaultModal({
      acceptButtonLabel: translate.Delete,
      message: translate.AreYouSureDeleteEmailTemplate,
      onAcceptClick: deleteEmailTemplate
    });
  };
  return (
    <>
      <div className="floatingBody p-4 pb-2">
        <div>
          {fields.map((x) => (
            <EmailTemplateFieldEdit
              key={x.title.replaceAll(" ", "")}
              {...{ ...x, currentEditorState, editorRef }}
            />
          ))}
          <FileListEditor
            files={current?.files?.map((x) => ({
              id: x.id.toString(),
              name: x.fileName
            }))}
            onFilesChange={(files) =>
              setEmailTemplate((email) => ({
                ...email,
                files: files.map((x) => ({
                  fileName: x.name,
                  response: null,
                  id: x.id
                }))
              }))
            }
            canEdit={CompanyService.canDo("edittask")}
            downloadFile={(fileId, fileName) =>
              FileService.downloadEmailTemplateFile(
                emailTemplate.MailTemplateID.toString(),
                fileId,
                fileName
              )
            }
          />
          <div className="form-group mb-3">
            <FileSingleEditor
              file={emailTemplate.FileLetter}
              onFileChange={(file) =>
                setEmailTemplate((email) => ({ ...email, FileLetter: file }))
              }
              canEdit={CompanyService.canDo("edittask")}
              validExtensions={["doc", "docx"]}
              invalidExtentionMessage={translate.WarningTheFileHasToBeDOCType}
              buttonText={
                <>
                  <i className="far fa-file-word me-2"></i>{" "}
                  {translate.UploadWordTemplate}
                </>
              }
              downloadFile={(fileId, fileName) =>
                FileService.download(fileId, fileName)
              }
            />
            <div className="d-flex align-items-center mb-2">
              <label className="me-2">
                {translate.AutomaticLetterTemplate}
              </label>
              <TooltipComponent
                title={translate.AutomaticLetterTemplateTooltip}
              >
                <i
                  className="far fa-question-circle text-muted"
                  style={{ cursor: "help" }}
                ></i>
              </TooltipComponent>
            </div>
          </div>
        </div>
      </div>
      <FloatingPanelFooter>
        {current && (
          <button type="button" className="btn btn-danger" onClick={onDelete}>
            {translate.Delete}
            {submiting === "del" && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </button>
        )}
        <button className="btn btn-primary me-3" onClick={onSubmit}>
          {translate.Save}
          {submiting === "set" && (
            <i className="fas fa-spinner-third fa-spin third ms-2"></i>
          )}
        </button>
      </FloatingPanelFooter>
    </>
  );
};

const EmailTemplateFieldEdit = ({
  type,
  title,
  onChange,
  items,
  defaultValue = undefined,
  currentEditorState,
  editorRef,
  maxLength
}: {
  onChange: (value?: string) => void;
  type: DataType;
  title: string;
  items: { value: string | number; text: string }[];
  defaultValue?: string;
  currentEditorState?: any;
  editorRef?: any;
  maxLength?: number;
}) => {
  let editor = undefined;
  let validationMethod: ValidationMessageProps<string>["validationMethod"] =
    undefined;
  let divClass = "";

  switch (type) {
    case DataType.Client:
      editor = (
        <AutocompleteClient onChange={onChange} defaultValue={defaultValue} />
      );
      break;
    case DataType.Group:
      editor =
        items.length === 1 ? (
          <AutocompleteGroup
            onChange={(value) => onChange(value!.value!)}
            clientId={items[0].text}
          />
        ) : (
          <input
            type="text"
            readOnly
            className="form-control-plaintext"
          ></input>
        );
      break;
    case DataType.List:
      if (items.length === 0) throw new Error("items missing");
      editor = (
        <Dropdown
          onChange={(x) => onChange(x !== undefined ? x.toString() : undefined)}
          items={items}
          defaultValue={defaultValue}
        />
      );
      break;
    case DataType.Date:
      editor = (
        <DatePickerEditor
          onChange={(x) => onChange(formatDateDigits(new Date(x), "en"))}
          defaultValue={defaultValue}
        />
      );
      break;
    case DataType.Number:
    case DataType.Currency:
      editor = (
        <NumberInput
          onChange={(event) => onChange(event.target.value)}
          allowDecimal
          allowNegative
          className="form-control"
          defaultValue={defaultValue}
        />
      );
      break;
    case DataType.Text:
      editor = maxLength ? (
        <TextEditor
          onChange={onChange}
          defaultValue={defaultValue}
          maxLength={maxLength}
        />
      ) : (
        <TextEditor onChange={onChange} defaultValue={defaultValue} />
      );
      validationMethod = (value: string | undefined) =>
        value !== undefined && value.length > 0;
      break;
    case DataType.YesNo:
      editor = (
        <CheckBoxEditor
          onChange={(x) => onChange(x as unknown as string)}
          defaultValue={defaultValue?.toString() === "true"}
          message={""}
        />
      );
      break;
    default:
      throw new Error("Missing editor: " + DataType[type]);
  }
  if (title === "texteditor") {
    // editor = <textarea onChange={event => onChange(event.target.value)} className="form-control h-100" style={{ resize: "none" }} value={defaultValue} id="content-texteditor" rows={6} />;
    editor = (
      <EmailEditor
        toolbar={toolbar}
        wrapperClassName="rdw‐wrapper‐noext" // para el contenedor
        editorClassName="rdw‐editor‐noext" // para el div editable
        defaultEditorState={currentEditorState.current}
        onEditorStateChange={(editorState) => {
          const rawContentState = convertToRaw(editorState.getCurrentContent());
          const markup = draftToHtml(rawContentState, {}, false);
          onChange(markup);
          currentEditorState.current = editorState;
        }}
        locale={TranslationService.currentLanguage}
        ref={(x) => (editorRef.current = Cast<EditorType>(x))}
      />
    );
    title = "";
    divClass = " flex-grow-1";
  }
  useEffect(() => {
    // Busca el div contentEditable dentro de nuestra clase
    const editorNode = document.querySelector<HTMLElement>(
      ".rdw‐editor‐noext [contenteditable='true']"
    );
    if (!editorNode) return;

    // Atributos anti‑Grammarly
    editorNode.setAttribute("data-gramm", "false");
    editorNode.setAttribute("data-gramm_editor", "false");
    editorNode.setAttribute("data-enable-grammarly", "false");

    // Atributos anti‑Translate u otras inyecciones
    editorNode.setAttribute("spellcheck", "false");
    editorNode.setAttribute("autocorrect", "off");
    editorNode.setAttribute("autocomplete", "off");
    editorNode.setAttribute("autocapitalize", "off");
    editorNode.setAttribute("translate", "no");
  }, []); // sólo al montar

  return (
    <div className={"form-group mb-3" + divClass}>
      {title && <label>{title}</label>}
      {editor}
      {
        <ValidationMessage
          onChange={onChange}
          validationMethod={validationMethod}
          defaultValue={defaultValue}
        />
      }
    </div>
  );
};
export default EmailTemplateEdit;

function from(
  current: EmailTemplateItem | undefined
): EmailTemplateItem & { FileLetter?: FileApi } {
  if (current) {
    // Si current tiene un archivo de carta (FileLetter), lo convertimos al formato FileApi
    // El backend devuelve FileLetter con estructura { fileName, id } (puede no tener response)
    if (current.FileLetter) {
      return {
        ...current,
        FileLetter: {
          id: current.FileLetter.id,
          name: current.FileLetter.fileName
        } as FileApi
      };
    }
    return current as EmailTemplateItem & { FileLetter?: FileApi };
  }
  return {
    Content: "",
    Created: new Date().toJSON(),
    CreatorUserID: Number(CompanyService.getUserid()),
    files: [],
    MailTemplateAttachments: null,
    MailTemplateID: undefined as unknown as number,
    MailTemplateID_ref: null,
    Name: "",
    Subject: "",
    ActivityTypeID: undefined,
    FileLetter: undefined
  };
}
